/*
 * Copyright 2022. NXGN Management, LLC. All Rights Reserved.
 */

import { Component, EventEmitter } from "@angular/core";
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from "@angular/forms";
import { BsModalRef } from "ngx-bootstrap/modal";
import { EditType, PartnerCredentials } from "../../../../models";
import { ActionModalComponent } from "../../../action-modal/action-modal.component";
import { NotificationService, PartnerService } from "../../../../services";

@Component({
  selector: "app-add-edit-partners",
  templateUrl: "./add-edit-partners.component.html"
})
export class AddEditPartnersComponent extends ActionModalComponent {
  partnersFormGroup!: FormGroup;
  partner!: PartnerCredentials;
  partners!: PartnerCredentials[];
  merchantId!: number;
  public event: EventEmitter<PartnerCredentials> = new EventEmitter();
  constructor(
    bsModal: BsModalRef,
    private fb: FormBuilder,
    private partnerService: PartnerService,
    private notificationService: NotificationService
  ) {
    super(bsModal);
  }

  ngOnInit(): void {
    this.partnersFormGroup = this.fb.group({
      username: [
        this.partner ? this.partner.username : "",
        [Validators.required, Validators.maxLength(25), this.userNameExists()]
      ],
      password: [this.partner ? this.partner.password : ""]
    });
  }

  okBasedOnAction(action: EditType): void {
    if (this.partnersFormGroup.valid) {
      switch (action) {
        case EditType.add:
          this.partnerService.create(this.merchantId, this.createPartnerCredentialsFromForm()).subscribe(
            (partnerCreds) => {
              this.handleCreateUpdateSuccess(partnerCreds);
            },
            (error) => {
              this.handleError(error);
            }
          );
          break;
        case EditType.edit:
          this.partnerService.update(this.merchantId, this.setPartnerCredentialsFromForm()).subscribe(
            (partnerCreds) => {
              this.handleCreateUpdateSuccess(partnerCreds);
            },
            (error) => {
              this.handleError(error);
            }
          );
          break;
        case EditType.remove:
          this.partnerService.delete(this.merchantId, this.partner.id).subscribe(
            () => {
              this.handleCreateUpdateSuccess(this.partner);
            },
            (error) => {
              this.handleError(error);
            }
          );
          break;
        case EditType.resetPassword:
          this.partnerService.resetPassword(this.merchantId, this.partner.id).subscribe(
            (partnerCreds) => {
              this.handleCreateUpdateSuccess(partnerCreds);
            },
            (error) => {
              this.handleError(error);
            }
          );
          break;
      }
    } else {
      this.partnersFormGroup.markAllAsTouched();
      this.actionInProgress = false;
    }
  }

  setPartnerCredentialsFromForm(): PartnerCredentials {
    const tempPartner: PartnerCredentials = JSON.parse(JSON.stringify(this.partner));
    tempPartner.username = this.partnersFormGroup.controls.username.value;
    return tempPartner;
  }

  createPartnerCredentialsFromForm(): any {
    return {
      username: this.partnersFormGroup.controls.username.value
    };
  }

  handleCreateUpdateSuccess(partnerCredentials: PartnerCredentials) {
    this.event.emit(partnerCredentials);
    this.actionInProgress = false;
    this.close();
  }

  handleError(error: any) {
    this.notificationService.error(error.error);
    this.actionInProgress = false;
  }

  showPassword(partner: PartnerCredentials) {
    this.partner.showPassword = !partner.showPassword;
  }

  userNameExists(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (
        this.partners.some((partner) => {
          if (this.partner) {
            return partner.id !== this.partner.id && partner.username === control.value;
          } else {
            return partner.username === control.value;
          }
        })
      ) {
        return { userNameExists: true };
      }
      return null;
    };
  }
}
